import { useState, ReactNode, useContext, useMemo, useEffect } from 'react';
import cn from 'classnames';
import {
  motion,
  LayoutGroup,
  AnimatePresence,
  useReducedMotion
} from 'framer-motion';
import { css, StyleSheet } from 'aphrodite';
import Button from './button';
import Modal from '../modals/modal';
import { ThemeContext } from '../../../utils/providers/theme-context';
import { useAppSearchParams } from '../../../utils/hooks/use-app-search-params';

export type Tab = {
  id: number;
  name: string | ReactNode;
  component?: ReactNode;
};

interface Props {
  tabs: Tab[];
  children?: (tab: Tab) => ReactNode;
  searchParams?: boolean;
  minHeight?: string;
  maxHeight?: string;
}

export default function Tabs({
  tabs,
  children,
  searchParams,
  minHeight,
  maxHeight
}: Props) {
  const themeCtx = useContext(ThemeContext)!;
  const { getColour, theme, getContrastTextColor } = themeCtx;
  const { searchParams: queryParams, setSearchParams } = useAppSearchParams();

  const primary = getColour('activeText');
  const text = getColour('normalText');
  // Inactive tab labels used to render at ``normalText`` (very dark navy
  // in light mode), which read as near-black next to the softer greys the
  // rest of the app uses for muted chrome. Use ``inactiveText`` — the
  // same colour secondary chrome (subtitles, help copy, muted badges)
  // uses — so the tab strip matches the rest of the app.
  const inactiveTabText = getColour('inactiveText');
  const secondLayer = getColour('secondLayer');
  const border = getColour('border');
  const activeTabText = theme === 'dark' ? text : getContrastTextColor(primary);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        shell: { color: text },
        border: { borderColor: border }
      }),
    [getColour]
  );

  const initial = useMemo(() => {
    if (searchParams && queryParams.tab !== undefined)
      return Number(queryParams.tab) || 0;
    return 0;
  }, [searchParams, queryParams.tab]);

  const [activeTab, setActiveTab] = useState<number>(initial);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (searchParams && queryParams.tab !== undefined) {
      const id = Number(queryParams.tab) || 0;
      if (id !== activeTab) setActiveTab(id);
    }
  }, [queryParams.tab, searchParams]);

  const tabIsActive = (tabId: number) =>
    searchParams
      ? queryParams.tab === undefined
        ? tabId === 0
        : tabId === Number(queryParams.tab)
      : tabId === activeTab;

  const handleTabChange = (tabId: number) => {
    setActiveTab(tabId);
    if (searchParams) setSearchParams({ tab: tabId.toString() });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const idx = tabs.findIndex(t => t.id === activeTab);
    if (idx < 0) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleTabChange(tabs[Math.min(idx + 1, tabs.length - 1)].id);
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handleTabChange(tabs[Math.max(idx - 1, 0)].id);
    }
  };

  const activeName = tabs.find(t => t.id === activeTab)?.name ?? '';
  const prefersReducedMotion = useReducedMotion();

  return (
    <div
      className={cn('flex flex-col', css(styles.shell))}
      style={{
        minHeight: minHeight || '72vh',
        maxHeight: maxHeight || '90vh',
        height: '100%'
      }}
    >
      <div className="flex-shrink-0">
        <div className="sm:hidden px-4 pb-2">
          <Button
            variant="light"
            size="small"
            label={activeName}
            onClick={() => setMobileOpen(true)}
            className="w-full justify-between"
          >
            {/* Chevron passes as children (not icon) so the Button
                keeps the label visible on mobile — ``icon`` triggers
                the "icon-only on narrow viewports" treatment, which
                would turn this dropdown trigger into a bare arrow. */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6 8l4 4 4-4"
                stroke={text}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
          <Modal
            isOpen={mobileOpen}
            onClose={() => setMobileOpen(false)}
            size="small"
            title={activeName}
            caption={''}
            footerActions={[
              <Button
                key="close"
                variant="smoke"
                size="small"
                label="Close"
                onClick={() => setMobileOpen(false)}
              />
            ]}
          >
            <div className="grid gap-2">
              {tabs.map(tab => {
                const active = tabIsActive(tab.id);
                return (
                  <Button
                    key={tab.id}
                    variant={active ? 'primary' : 'light'}
                    size="small"
                    label={tab.name}
                    onClick={() => {
                      handleTabChange(tab.id);
                      setMobileOpen(false);
                    }}
                    className="w-full justify-start cursor-pointer select-none"
                  />
                );
              })}
            </div>
          </Modal>
        </div>

        {/* Desktop */}
        <div className="hidden sm:block">
          <nav
            aria-label="Tabs"
            role="tablist"
            onKeyDown={onKeyDown}
            className="pb-2"
          >
            <LayoutGroup id="tabs-nav">
              <div className="flex gap-2 overflow-x-auto py-3 px-1 scrollbar-none">
                {tabs.map(tab => {
                  const active = tabIsActive(tab.id);
                  return (
                    <motion.button
                      type="button"
                      layout="position"
                      key={tab.id}
                      role="tab"
                      aria-selected={active}
                      aria-controls={`panel-${tab.id}`}
                      id={`tab-${tab.id}`}
                      onClick={() => handleTabChange(tab.id)}
                      className={cn(
                        'relative inline-flex items-center justify-center',
                        'px-4 py-1.5 text-sm font-medium rounded-2xl',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                        theme === 'dark'
                          ? 'focus-visible:ring-offset-gray-900'
                          : 'focus-visible:ring-offset-white',
                        'cursor-pointer select-none',
                        'flex-none shrink-0',
                        active ? '' : 'border border-transparent'
                      )}
                      style={{
                        minWidth: 100,
                        borderColor: active ? 'transparent' : border,
                        boxShadow:
                          '0 4px 12px 2px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.06)'
                      }}
                      initial={false}
                      animate={{
                        backgroundColor: active ? primary : secondLayer,
                        color: active ? activeTabText : inactiveTabText
                      }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      whileHover={
                        prefersReducedMotion || active
                          ? undefined
                          : { boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }
                      }
                      whileTap={
                        prefersReducedMotion ? undefined : { scale: 0.97 }
                      }
                    >
                      <span className="relative z-10 whitespace-nowrap font-semibold">
                        {tab.name}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </LayoutGroup>
          </nav>

          <div className="h-px w-full" style={{ backgroundColor: border }} />
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex-grow min-h-0 flex flex-col">
        <AnimatePresence initial={false} mode="wait">
          {tabs.map(tab =>
            tabIsActive(tab.id) ? (
              <motion.div
                key={tab.id}
                role="tabpanel"
                id={`panel-${tab.id}`}
                aria-labelledby={`tab-${tab.id}`}
                initial={{
                  opacity: 0,
                  y: prefersReducedMotion ? 0 : 4
                }}
                animate={{ opacity: 1, y: 0 }}
                exit={{
                  opacity: 0,
                  y: prefersReducedMotion ? 0 : -4
                }}
                transition={{ duration: prefersReducedMotion ? 0 : 0.18 }}
                className="flex-grow min-h-0 flex flex-col"
              >
                <div
                  className="flex-grow min-h-0 overflow-auto flex flex-col"
                  style={{ scrollbarGutter: 'stable' }}
                >
                  {tab.component ?? children?.(tab)}
                </div>
              </motion.div>
            ) : null
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
